import { startOfDay, endOfDay } from 'date-fns';
import { DatePickerField } from '@/shared/components/forms/DatePickerField';

/**
 * Filtru de interval de date, in antetul unei coloane de raport.
 *
 * SE-30 (owner, 2026-09-16): `input[type=datetime-local]` inlocuit cu `DatePickerField`
 * (`dd-MM-yyyy` — un filtru de raport cere ziua, nu ora; timpul de pe camp era neintentionat,
 * niciun utilizator nu introducea o ora anume). Efect colateral util: `DatePickerField` e un
 * `<button>`, nu un `<input>` de text, deci capcana de zoom iOS pe focus (12px, reparata in
 * faza 3.0) e structural imposibila aici — nu mai exista camp text de focusat.
 *
 * ⚠️ Granularitatea de ZI schimba semnificatia capetelor: „De la" ramane inceputul zilei
 * (00:00:00, ca inainte — o zi selectata include ora 00:00 a ei), dar „Până la" trebuie sa
 * devina SFARSITUL zilei (23:59:59), altfel ziua aleasa ar fi exclusa din filtru (backendul
 * compara pe interval [start, end], iar 00:00:00 al zilei "Până la" ar taia restul zilei).
 */
interface TableDateRangeFilterProps {
    fromName: string;      // e.g., "timestampInregistrare.start"
    toName: string;        // e.g., "timestampInregistrare.end"
    from: string | number;
    to: string | number;
    disabled?: boolean;
    handleChange: (e: any) => void;
}

export function TableDateRangeFilter({
    fromName,
    toName,
    from,
    to,
    disabled,
    handleChange
}: TableDateRangeFilterProps) {
    const timestampLaData = (timestamp: string | number): Date | undefined => {
        if (!timestamp) return undefined;
        const ts = typeof timestamp === 'string' ? parseInt(timestamp) : timestamp;
        if (isNaN(ts)) return undefined;
        return new Date(ts * 1000);
    };

    const handleFromChange = (data: Date | undefined) => {
        const timestamp = data ? Math.floor(startOfDay(data).getTime() / 1000) : '';
        handleChange({ target: { name: fromName, value: timestamp } });
    };

    const handleToChange = (data: Date | undefined) => {
        const timestamp = data ? Math.floor(endOfDay(data).getTime() / 1000) : '';
        handleChange({ target: { name: toName, value: timestamp } });
    };

    return (
        <div className="flex flex-col gap-2">
            <DatePickerField
                value={timestampLaData(from)}
                onChange={handleFromChange}
                label="De la:"
                maxDate={timestampLaData(to)}
                disabled={disabled}
                displayFormat="dd-MM-yyyy"
                placeholder="Selectați ziua"
            />
            <DatePickerField
                value={timestampLaData(to)}
                onChange={handleToChange}
                label="Până la:"
                minDate={timestampLaData(from)}
                disabled={disabled}
                displayFormat="dd-MM-yyyy"
                placeholder="Selectați ziua"
            />
        </div>
    );
}
